// Helpers for device-level NTP servers (config.network_attributes.ntp.server),
// exposed by the tree-mgmt config API as `ntp_servers`. Mirrors the backend
// normalization so the UI can detect real changes and validate before saving.

/** Maximum number of NTP servers per device (keep in sync with the backend). */
export const MAX_NTP_SERVERS = 4;

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const HOST_LABEL_RE = /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

function isValidIPv6(value) {
  if (!value.includes(':') || !/^[0-9A-Fa-f:.]+$/.test(value)) return false;
  try {
    // URL parsing rejects malformed IPv6 literals.
    return new URL(`http://[${value}]/`).hostname.length > 0;
  } catch {
    return false;
  }
}

/** True for an IPv4/IPv6 address or an RFC 1123 hostname. */
export function isValidNtpServer(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return false;
  if (IPV4_RE.test(value) || isValidIPv6(value)) return true;
  if (value.length > 253) return false;
  const labels = value.split('.');
  if (!labels.every((l) => HOST_LABEL_RE.test(l))) return false;
  // An all-numeric dotted name is a malformed IPv4 address, not a hostname.
  return !/^\d+$/.test(labels[labels.length - 1]);
}

/** Trim, drop blanks and case-insensitive duplicates, keep order. */
export function normalizeNtpServers(list) {
  const out = [];
  const seen = new Set();
  for (const entry of list || []) {
    const value = String(entry ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** Order-sensitive comparison after normalization (order is priority). */
export function ntpServersEqual(a, b) {
  const na = normalizeNtpServers(a);
  const nb = normalizeNtpServers(b);
  return na.length === nb.length && na.every((v, i) => v === nb[i]);
}

/** Error messages for each non-blank invalid entry. */
export function ntpServerErrors(list) {
  const errors = (list || [])
    .map((entry) => String(entry ?? '').trim())
    .filter((value) => value && !isValidNtpServer(value))
    .map((value) => `NTP: "${value}" is not a valid hostname or IP address`);
  if (normalizeNtpServers(list).length > MAX_NTP_SERVERS) {
    errors.push(`NTP: at most ${MAX_NTP_SERVERS} servers are allowed`);
  }
  return errors;
}
